import { useEffect, useMemo, useRef, useState } from "react";

import { useFetcher } from "@remix-run/react";
import type { z } from "zod";

import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Label } from "~/components";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
    browseVisitDescriptionSchema,
    cartPrimaryReasonSchema,
    type ExitSurveyPayload,
    type ExitSurveyScenarioWire,
} from "~/lib/exit-survey";
import { markExitSurveySnoozed, markExitSurveySubmitted, type ExitSurveyTriggerId } from "~/lib/exit-survey-gate";
import { cn } from "~/lib/utils";
import { pushDataLayerEvent } from "~/utils/analytics.client";

type CartReason = z.infer<typeof cartPrimaryReasonSchema>;
type BrowseVisit = z.infer<typeof browseVisitDescriptionSchema>;
type ContactPref = ExitSurveyPayload["contactPreference"];

const CART_REASONS: { value: CartReason; label: string }[] = [
    { value: "browsing_no_plan_to_buy", label: "I was just browsing and didn’t plan to buy anything" },
    { value: "more_questions", label: "I have more questions" },
    {
        value: "not_comfortable_without_visit",
        label: "I am not comfortable buying without seeing the location in person",
    },
    { value: "something_else", label: "Something else" },
];

const BROWSE_VISITS: { value: BrowseVisit; label: string }[] = [
    { value: "just_browsing", label: "I was just browsing" },
    {
        value: "gathering_info_before_call_or_visit",
        label: "I was gathering information before calling or visiting your location",
    },
    { value: "researching_pre_arranged", label: "I’m researching options for pre-arranged services" },
    {
        value: "hoped_to_purchase_couldnt_figure_out",
        label: "I was hoping to purchase something but couldn’t figure it out",
    },
    { value: "something_else", label: "Something else" },
];

const CONTACT_OPTIONS: { value: ContactPref; label: string }[] = [
    { value: "text", label: "Text" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone call" },
    { value: "none", label: "I don’t need anyone to contact me at this time" },
];

function RadioRow({ id, label, value }: { id: string; label: string; value: string }) {
    return (
        <div className="flex items-start gap-3">
            <RadioGroupItem value={value} id={id} className="mt-1 shrink-0" />
            <Label htmlFor={id} className="font-normal leading-snug cursor-pointer">
                {label}
            </Label>
        </div>
    );
}

export function ExitSurveyDialog({
    open,
    onOpenChange,
    scenario,
    pageUrl,
    triggerId,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    scenario: ExitSurveyScenarioWire;
    pageUrl: string;
    triggerId: ExitSurveyTriggerId | null;
}) {
    const fetcher = useFetcher<{ ok?: boolean; error?: string; issues?: unknown }>();
    const didSubmitRef = useRef(false);
    const pendingSubmitRef = useRef(false);

    const [primaryReason, setPrimaryReason] = useState<CartReason | "">("");
    const [primaryReasonOther, setPrimaryReasonOther] = useState("");
    const [visitDescription, setVisitDescription] = useState<BrowseVisit | "">("");
    const [visitDescriptionOther, setVisitDescriptionOther] = useState("");
    const [siteUnanswered, setSiteUnanswered] = useState<"yes" | "no" | "">("");
    const [siteUnansweredDetail, setSiteUnansweredDetail] = useState("");
    const [contactPreference, setContactPreference] = useState<ContactPref | "">("");
    const [contactName, setContactName] = useState("");
    const [contactValue, setContactValue] = useState("");
    const [showThankYou, setShowThankYou] = useState(false);

    useEffect(() => {
        if (!open) return;
        didSubmitRef.current = false;
        pendingSubmitRef.current = false;
        setShowThankYou(false);
        setPrimaryReason("");
        setPrimaryReasonOther("");
        setVisitDescription("");
        setVisitDescriptionOther("");
        setSiteUnanswered("");
        setSiteUnansweredDetail("");
        setContactPreference("");
        setContactName("");
        setContactValue("");
    }, [open, scenario]);

    useEffect(() => {
        if (!open || !pendingSubmitRef.current) return;
        if (fetcher.state === "idle" && fetcher.data?.ok) {
            pendingSubmitRef.current = false;
            didSubmitRef.current = true;
            markExitSurveySubmitted();
            pushDataLayerEvent("exit_survey_submitted", {
                scenario,
                trigger: triggerId ?? "unknown",
                contactPreference,
                hasContactValue: contactPreference !== "none",
                hasContactName: contactPreference !== "none" && Boolean(contactName.trim()),
                ...(scenario === "CART_ABANDON"
                    ? {
                          primaryReason,
                          hasPrimaryReasonOther: Boolean(primaryReasonOther.trim()),
                      }
                    : {
                          visitDescription,
                          siteUnansweredQuestions: siteUnanswered,
                          hasVisitOther: Boolean(visitDescriptionOther.trim()),
                          hasUnansweredDetail: Boolean(siteUnansweredDetail.trim()),
                      }),
            });
            setShowThankYou(true);
        }
    }, [
        open,
        fetcher.state,
        fetcher.data,
        scenario,
        triggerId,
        contactPreference,
        contactName,
        primaryReason,
        primaryReasonOther,
        visitDescription,
        visitDescriptionOther,
        siteUnanswered,
        siteUnansweredDetail,
    ]);

    const handleDialogOpenChange = (next: boolean) => {
        if (!next && open && !didSubmitRef.current) {
            markExitSurveySnoozed();
        }
        onOpenChange(next);
    };

    const canSubmit = useMemo(() => {
        if (!contactPreference) return false;
        if (contactPreference !== "none") {
            if (!contactName.trim() || !contactValue.trim()) return false;
        }
        if (scenario === "CART_ABANDON") {
            return !!primaryReason;
        }
        return !!(visitDescription && siteUnanswered);
    }, [
        scenario,
        primaryReason,
        visitDescription,
        siteUnanswered,
        contactPreference,
        contactName,
        contactValue,
    ]);

    const submit = () => {
        if (!canSubmit || !contactPreference) return;

        let payload: ExitSurveyPayload;
        const triggerField = triggerId ? { trigger: triggerId } : {};
        const contactFields =
            contactPreference !== "none" && contactName.trim() && contactValue.trim()
                ? { contactName: contactName.trim(), contactValue: contactValue.trim() }
                : {};

        if (scenario === "CART_ABANDON") {
            payload = {
                scenario: "CART_ABANDON",
                answers: {
                    primaryReason: primaryReason as CartReason,
                    ...(primaryReason === "something_else" && primaryReasonOther.trim()
                        ? { primaryReasonOther: primaryReasonOther.trim() }
                        : {}),
                },
                contactPreference,
                ...contactFields,
                ...(pageUrl ? { pageUrl } : {}),
                ...triggerField,
            };
        } else {
            payload = {
                scenario: "BROWSE_ABANDON",
                answers: {
                    visitDescription: visitDescription as BrowseVisit,
                    ...(visitDescription === "something_else" && visitDescriptionOther.trim()
                        ? { visitDescriptionOther: visitDescriptionOther.trim() }
                        : {}),
                    siteUnansweredQuestions: siteUnanswered as "yes" | "no",
                    ...(siteUnanswered === "yes" && siteUnansweredDetail.trim()
                        ? { siteUnansweredDetail: siteUnansweredDetail.trim() }
                        : {}),
                },
                contactPreference,
                ...contactFields,
                ...(pageUrl ? { pageUrl } : {}),
                ...triggerField,
            };
        }

        pendingSubmitRef.current = true;
        fetcher.submit(payload, {
            method: "POST",
            action: "/api/exit-survey",
            encType: "application/json",
        });
    };

    const serverError =
        fetcher.state === "idle" && fetcher.data && !fetcher.data.ok ? (fetcher.data.error ?? "Something went wrong") : null;

    const thankYouMessage =
        contactPreference === "none"
            ? "Thank you for your feedback."
            : "Thank you for your feedback. A representative from our location will be in touch with you shortly.";

    return (
        <Dialog open={open} onOpenChange={handleDialogOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
                <DialogHeader>
                    <DialogTitle>{showThankYou ? "Thank you" : "Quick feedback"}</DialogTitle>
                    {!showThankYou ? (
                        <DialogDescription>
                            {scenario === "CART_ABANDON"
                                ? "We’d value your input before you go."
                                : "Help us understand your visit before you go."}
                        </DialogDescription>
                    ) : null}
                </DialogHeader>

                {showThankYou ? (
                    <DialogDescription className="text-left text-foreground text-sm leading-relaxed">
                        {thankYouMessage}
                    </DialogDescription>
                ) : (
                    <div className="flex flex-col gap-8 text-left">
                        {scenario === "CART_ABANDON" ? (
                            <section className="flex flex-col gap-3">
                                <p className="text-sm font-medium text-foreground">
                                    Why have you decided NOT to purchase the items in your shopping cart?
                                </p>
                                <RadioGroup
                                    value={primaryReason}
                                    onValueChange={(v) => setPrimaryReason(v as CartReason)}
                                    className="gap-3"
                                >
                                    {CART_REASONS.map(({ value, label }) => (
                                        <RadioRow key={value} id={`cart-${value}`} value={value} label={label} />
                                    ))}
                                </RadioGroup>
                                {primaryReason === "something_else" ? (
                                    <div className="flex flex-col gap-2 pl-1">
                                        <Label htmlFor="primaryReasonOther">Please describe</Label>
                                        <Input
                                            id="primaryReasonOther"
                                            value={primaryReasonOther}
                                            onChange={(e) => setPrimaryReasonOther(e.target.value)}
                                            placeholder="Your answer"
                                        />
                                    </div>
                                ) : null}
                            </section>
                        ) : (
                            <>
                                <section className="flex flex-col gap-3">
                                    <p className="text-sm font-medium text-foreground">
                                        What best describes your visit to our website today?
                                    </p>
                                    <RadioGroup
                                        value={visitDescription}
                                        onValueChange={(v) => setVisitDescription(v as BrowseVisit)}
                                        className="gap-3"
                                    >
                                        {BROWSE_VISITS.map(({ value, label }) => (
                                            <RadioRow key={value} id={`browse-${value}`} value={value} label={label} />
                                        ))}
                                    </RadioGroup>
                                    {visitDescription === "something_else" ? (
                                        <div className="flex flex-col gap-2 pl-1">
                                            <Label htmlFor="visitDescriptionOther">Please describe</Label>
                                            <Input
                                                id="visitDescriptionOther"
                                                value={visitDescriptionOther}
                                                onChange={(e) => setVisitDescriptionOther(e.target.value)}
                                                placeholder="Your answer"
                                            />
                                        </div>
                                    ) : null}
                                </section>
                                <section className="flex flex-col gap-3">
                                    <p className="text-sm font-medium text-foreground">
                                        Do you have any questions our website was unable to answer?
                                    </p>
                                    <RadioGroup
                                        value={siteUnanswered}
                                        onValueChange={(v) => setSiteUnanswered(v as "yes" | "no")}
                                        className="gap-3"
                                    >
                                        <RadioRow id="site-unanswered-yes" value="yes" label="Yes" />
                                        <RadioRow id="site-unanswered-no" value="no" label="No" />
                                    </RadioGroup>
                                    {siteUnanswered === "yes" ? (
                                        <div className="flex flex-col gap-2 pl-1">
                                            <Label htmlFor="siteUnansweredDetail">Your question (optional)</Label>
                                            <Input
                                                id="siteUnansweredDetail"
                                                value={siteUnansweredDetail}
                                                onChange={(e) => setSiteUnansweredDetail(e.target.value)}
                                                placeholder="Optional details"
                                            />
                                        </div>
                                    ) : null}
                                </section>
                            </>
                        )}

                        <section className="flex flex-col gap-3">
                            <p className="text-sm font-medium text-foreground">How can we contact you to provide more help?</p>
                            <RadioGroup
                                value={contactPreference}
                                onValueChange={(v) => setContactPreference(v as ContactPref)}
                                className="gap-3"
                            >
                                {CONTACT_OPTIONS.map(({ value, label }) => (
                                    <RadioRow key={value} id={`contact-${value}`} value={value} label={label} />
                                ))}
                            </RadioGroup>
                            {contactPreference && contactPreference !== "none" ? (
                                <div className="flex flex-col gap-2 pl-1">
                                    <Label htmlFor="contactName">Name</Label>
                                    <Input
                                        id="contactName"
                                        value={contactName}
                                        onChange={(e) => setContactName(e.target.value)}
                                        placeholder="Your name"
                                        autoComplete="name"
                                    />
                                    <Label htmlFor="contactValue" className="mt-1">
                                        {contactPreference === "email"
                                            ? "Email address"
                                            : contactPreference === "phone"
                                              ? "Phone number"
                                              : "Mobile number for text"}
                                    </Label>
                                    <Input
                                        id="contactValue"
                                        value={contactValue}
                                        onChange={(e) => setContactValue(e.target.value)}
                                        placeholder={
                                            contactPreference === "email"
                                                ? "you@example.com"
                                                : contactPreference === "phone"
                                                  ? "Phone number"
                                                  : "Mobile number"
                                        }
                                    />
                                </div>
                            ) : null}
                        </section>

                        {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}

                        <div className="flex flex-wrap gap-3 justify-end">
                            <Button
                                type="button"
                                disabled={!canSubmit || fetcher.state !== "idle"}
                                variant={!canSubmit ? "disabled" : "default"}
                                className={cn(!canSubmit && "opacity-50")}
                                onClick={submit}
                            >
                                {fetcher.state !== "idle" ? "Sending…" : "Submit"}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
